import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";
import { KOMMUN_LINKS, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN, LAST_REVIEWED } from "@/lib/official-sources";

const faqs = [
  { q: "Vart ansöker jag om serveringstillstånd i Karlskoga?", a: "Ansökan görs till tillståndsenheten i Karlskoga kommun. Verksamhet i annan kommun (t.ex. Örebro, Degerfors, Kristinehamn) söker hos respektive kommun." },
  { q: "Vad kostar serveringstillstånd i Karlskoga?", a: "Karlskoga kommun tar ut en ansökningsavgift och en årlig tillsynsavgift beroende på tillståndstyp och alkoholomsättning – aktuella avgifter finns på kommunens webbplats." },
  { q: "Hur lång är handläggningstiden i Karlskoga?", a: "Räkna med 8–12 veckor för en komplett ansökan om stadigvarande tillstånd." },
];

export const Route = createFileRoute("/serveringstillstand-karlskoga")({
  component: () => (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Guider", to: "/serveringstillstand-och-kunskapsprov" },
        { label: "Karlskoga" },
      ]}
      title="Serveringstillstånd Karlskoga – ansökan och krav"
      intro="Allt du behöver veta om serveringstillstånd i Karlskoga: var du ansöker, vilka krav som ställs och hur du förbereder dig för kunskapsprovet."
      lastReviewed={LAST_REVIEWED}
      sources={[KOMMUN_LINKS.karlskoga, SOURCE_FOHM_VAGLEDNING, SOURCE_ALKOHOLLAGEN]}
      sections={[
        { h2: "Var ansöker du i Karlskoga?", body: "Tillståndsenheten i Karlskoga kommun ansvarar för serveringstillstånd. För verksamhet i grannkommuner som Degerfors, Kristinehamn eller Örebro ansöker du hos respektive kommun." },
        { h2: "Krav från Karlskoga kommun", body: "Utöver de nationella kraven (personlig och ekonomisk lämplighet, godkänt kunskapsprov och lämplig lokal) har Karlskoga en industripräglad stad med lunchrestauranger, personalrestauranger och evenemangsservering kring Bofors och Nobelmuseet. Från 1 juni 2026 är kraven på eget kök och varierat matutbud borttagna nationellt." },
        { h2: "Kunskapsprovet i Karlskoga", body: "Provet genomförs hos kommunen efter att ansökan har kommit in. Skriftligt prov inom alkohollagen, serveringsbestämmelser, ordning, nykterhet och marknadsföring enligt FoHMFS 2014:7." },
        { h2: "Handläggningstid", body: "Räkna med 8–12 veckor från komplett ansökan till beslut." },
        { h2: "Tillsyn och uppföljning", body: "Karlskoga kommun bedriver tillsyn tillsammans med polis och Skatteverket. Anmärkningar kan leda till erinran, varning, sanktionsavgift eller återkallat tillstånd." },
        { h2: "Lokala särdrag – Bofors och Alfred Nobel", body: "Karlskoga är starkt förknippat med Bofors och Alfred Nobels historia. Industrikonferenser och personalrestauranger vid större arbetsplatser skapar egna villkor för serveringstillstånd – kontakta tillståndsenheten tidigt vid frågor om slutna sällskap och personalservering." },
      ]}
      faqs={faqs}
      related={{
        title: "Relaterade sidor",
        links: [
          { to: "/serveringstillstand-och-kunskapsprov", label: "Hub – alla guider" },
          { to: "/serveringstillstand-orebro", label: "Serveringstillstånd Örebro" },
          { to: "/serveringstillstand-karlstad", label: "Serveringstillstånd Karlstad" },
          { to: "/serveringstillstand-restaurang", label: "För restaurang" },
          { to: "/serveringstillstand-catering", label: "För catering" },
          { to: "/tillfalligt-serveringstillstand", label: "Tillfälligt tillstånd" },
          { to: "/kunskapsprov-alkohollagen", label: "Kunskapsprov i alkohollagen" },
          { to: "/foretag", label: "Företagslösning" },
        ],
      }}
    />
  ),
  head: () => ({
    meta: [
      { title: "Serveringstillstånd Karlskoga – krav, prov & tips" },
      { name: "description", content: "Så får du serveringstillstånd i Karlskoga: lokala krav, tillsynsavgifter och hur du klarar kunskapsprovet." },
      { property: "og:title", content: "Serveringstillstånd Karlskoga – tips" },
      { property: "og:description", content: "Ansökan, krav, avgifter och kunskapsprov för serveringstillstånd i Karlskoga." },
      { property: "og:url", content: "https://alkoholprovet.se/serveringstillstand-karlskoga" },
    ],
    links: [{ rel: "canonical", href: "https://alkoholprovet.se/serveringstillstand-karlskoga" }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Guider", url: "https://alkoholprovet.se/serveringstillstand-och-kunskapsprov" },
        { label: "Karlskoga", url: "https://alkoholprovet.se/serveringstillstand-karlskoga" },
      ]),
    ],
  }),
});
